import * as openpgp from "openpgp";
import { ICompanyKey, ICompanyKeysDecryptResult } from "dto/ICompanyKey";
import { IDecryptedKeys } from "./decryptKeys";
import { readKeyMaterial } from "./readPublicKey";

// Odszyfrowanie klucza prywatnego FIRMY z grantu (docs/model-kluczy-firmowych.md, sekcja 3
// „Przywracanie kluczy i logowanie na drugim urządzeniu", punkt 4).
//
// Grant to `PGP(intermediate.pub CZŁONKA){ company.priv }` w base64 — czyli klucz firmy
// NIE ISTNIEJE jako jeden blob (I4). Odtwarzamy go z grantu przy każdym starcie sesji
// i NIGDY nie zapisujemy do localStorage: odebranie grantu odcina dostęp po pierwszym
// odświeżeniu, bez czekania na rotację.
//
// Wynik jest ROZDZIELONY: klucze osobno, nieudane generacje osobno. To celowe — błąd
// deszyfracji grantu ma trafić na <CompanyKeyErrorGate/> jako JAWNY błąd, a nie zamienić
// się w cichy fallback wyglądający jak „administrator jeszcze nie wydał klucza".
//
// W FAZIE 2 ten moduł może nie mieć konsumenta: treść nadal szyfruje klucz PROFILU, a slot
// 'company' mirrora contentKeys jest karmiony kluczami profilu. Konsument (CompanyProvider)
// wchodzi razem z przełączeniem zapisu w F3.

/** Błąd odszyfrowania grantu klucza firmy. */
export class CompanyKeyDecryptError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CompanyKeyDecryptError";
  }
}

const base64ToBytes = (base64: string): Uint8Array => {
  const binary = atob(base64.replace(/\s+/g, ""));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

/**
 * Klucz POŚREDNI zalogowanego — jedyne, czym da się rozpakować grant. Leży w pakiecie
 * z getKeys jako base64 ASCII-armoru; readKeyMaterial przyjmuje oba formaty i RZUCA,
 * gdy nie wyszedł z tego klucz (zamiast oddać undefined).
 */
const readIntermediateKey = async (
  myKeys: IDecryptedKeys | null | undefined,
): Promise<openpgp.key.Key> => {
  const material = myKeys?.intermidatePrivateKey?.data;
  if (!material) {
    throw new CompanyKeyDecryptError(
      "Brak klucza pośredniego — nie ma czym rozpakować grantu klucza firmy",
    );
  }

  try {
    return await readKeyMaterial(material);
  } catch (error) {
    throw new CompanyKeyDecryptError(
      `Klucz pośredni jest nieczytelny: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }
};

/** Rozpakowuje JEDEN grant. Rzuca, gdy grant nie należy do tego klucza pośredniego. */
export const decryptCompanyKeyGrant = async (
  grant: string,
  intermediateKey: openpgp.key.Key,
): Promise<openpgp.key.Key> => {
  const message = await openpgp.message.read(base64ToBytes(grant));

  const { data } = await openpgp.decrypt({
    message,
    privateKeys: [intermediateKey],
    format: "binary",
  });

  const key = await readKeyMaterial(new Uint8Array(data as Uint8Array));

  // Klucz firmy generuje FE bez hasła; wariant „chroniony pustym hasłem" (S2K usage 254,
  // rozstrzygnięcie Q17) odblokowuje się REALNIE asynchronicznie — stąd await.
  if (!key.isDecrypted()) {
    try {
      await key.decrypt("");
    } catch {
      // Zostawiamy zamknięty — openpgp odrzuci go przy deszyfracji, i to jest błąd jawny.
    }
  }

  return key;
};

/**
 * Odszyfrowuje klucze prywatne firmy ze WSZYSTKICH generacji, na które zalogowany ma grant.
 *
 * Generacje bez grantu (`MyGrant === null`) są POMIJANE i NIE są porażką — to stan
 * „czekam na wydanie klucza" (<AwaitingCompanyKeyGate/>). Porażką jest grant, którego nie
 * dało się rozpakować: taka generacja ląduje w `FailedGenerations`.
 */
export const decryptCompanyPrivateKeys = async (
  companyKeys: ICompanyKey[] | null | undefined,
  myKeys: IDecryptedKeys | null | undefined,
): Promise<ICompanyKeysDecryptResult<openpgp.key.Key>> => {
  const result: ICompanyKeysDecryptResult<openpgp.key.Key> = {
    Keys: [],
    FailedGenerations: [],
  };

  const granted = (companyKeys ?? []).filter((key) => !!key?.MyGrant);
  if (!granted.length) return result;

  const intermediateKey = await readIntermediateKey(myKeys);

  for (const companyKey of granted) {
    try {
      result.Keys.push(
        await decryptCompanyKeyGrant(companyKey.MyGrant!, intermediateKey),
      );
    } catch (error) {
      console.error(
        `[companyKeys] grant generacji ${companyKey.Generation} nieodszyfrowywalny`,
        error,
      );
      result.FailedGenerations.push(companyKey.Generation);
    }
  }

  return result;
};
